<template>
  <div class="print-hide">
    <q-drawer
      v-model="menuOpen"
      class="sidemenu"
      show-if-above
      :mini="isMenuMini"
      mini-to-overlay
      :mini-width="65"
      :width="$q.screen.lt.md ? $q.screen.width : 300"
      side="left"
      bordered
    >
      <div class="drawer-content">
        <div class="drawer-scroll">
          <q-list v-if="$q.screen.lt.md">
            <q-item
              clickable
              @click="menuOpen = false"
            >
              <q-item-section />
              <q-item-section avatar>
                <re-icon
                  size="md"
                  color="primary"
                  name="mdi-menu-open"
                />
              </q-item-section>
            </q-item>
            <q-separator />
            <q-item v-if="userIs.dvmValuer && authenticated">
              <go-to-dvm-input class="full-width" />
            </q-item>
            <q-item v-if="authenticated">
              <new-request-button
                :label="newRequestButtonLabel"
                size="sm"
                :collapse-to-project="true"
                class="full-width"
                btn-classes="full-width"
              />
            </q-item>
          </q-list>
          <q-list @mouseleave="onMouseLeave">
            <q-item
              v-for="item in menuItems.filter((i) => i.show !== false)"
              :key="item.label"
              clickable
              :active="route.name === item.route"
              :to="{ name: item.route, params: item.params }"
              class="sidemenu-item"
              @click="item.click"
              @mouseover="onMouseOver"
            >
              <q-item-section avatar>
                <re-icon
                  :name="item.icon"
                  :indicator="item.indicator"
                  :self-center="false"
                  color="inherit"
                />
              </q-item-section>
              <q-item-section>
                <q-item-label :class="{ 'text-weight-bold': route.name === item.route }">
                  {{ t(item.label) }}
                </q-item-label>
              </q-item-section>
            </q-item>
          </q-list>
        </div>
        <q-card
          v-if="$q.screen.lt.md"
          class="q-ma-md"
          flat
        >
          <navbar-items />
        </q-card>
      </div>
    </q-drawer>
  </div>
</template>
<script setup lang="ts">
import { useQuasar } from 'quasar'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

import { useConfig } from '@/composables/useConfig'
import { useCurUser } from '@/composables/useCurUser'
import { useDashboardRequestList } from '@/composables/useDashboardRequestList'
import enums from '@/plugins/enums'

import GoToDvmInput from '../GoToDvmInput.vue'
import NavbarItems from '../NavbarItems.vue'
import NewRequestButton from '../NewRequestButton.vue'

const { t } = useI18n()
const config = useConfig()
const route = useRoute()
const { is: userIs, hasRole, submitableModules, accessibleModules, authenticated, userId } = useCurUser()

const $q = useQuasar()
const newRequestButtonLabel = computed(() => {
  const isOnlyCatalogUser =
    accessibleModules.value.length === 1 && accessibleModules.value.includes(enums.modules.CAT)
  return isOnlyCatalogUser ? t('catalog.sidebar.new_button') : t('dashboard.new_request.title')
})

const menuOpen = defineModel('menuOpen', { type: Boolean })

const isMenuMini = ref(true)

const requestList = userIs.value.onlyBorrower ? useDashboardRequestList() : undefined

let onMouseOverTimeout: ReturnType<typeof setTimeout> | null = null

const onMouseOver = () => {
  if (onMouseOverTimeout) clearTimeout(onMouseOverTimeout)
  onMouseOverTimeout = setTimeout(() => {
    isMenuMini.value = false
  }, 500)
}

const onMouseLeave = () => {
  if (onMouseOverTimeout) clearTimeout(onMouseOverTimeout)
  isMenuMini.value = true
}

const menuItems = computed(() => [
  {
    icon: 'ic-apps',
    label: 'section.apps',
    route: 'appDashboard',
    show: submitableModules.value.length > 1,
  },
  {
    icon: 'ic-monitor',
    label: 'section.dashboard',
    route: 'dashboard',
    // FIXME 15/05/2025 : The same condition is duplicated in Dashboard.vue to redirect to the borrower flow. We should extract and reuse the same logic in both place
    show: !userIs.value.onlyBorrower || requestList?.data?.value?.length !== 1,
  },
  {
    icon: 'mdi-account',
    label: 'monolith.shared.valuer',
    route: 'expert.dashboard',
    params: { userId: userId.value },
    show: userIs.value.ovmValuer || userIs.value.dvmValuer || userIs.value.oepcValuer,
  },
  {
    icon: 'mdi-account-supervisor',
    label: 'section.dispatcher',
    route: 'dispatcher',
    show: userIs.value.onsiteDispatcher || userIs.value.dvmDispatcher,
  },
  {
    icon: 'mdi-assistant',
    label: 'home_companion.assistant.name',
    route: 'knowledge-base-assistant',
    show: userIs.value.catSubmitter,
  },
  {
    icon: 'mdi-bookshelf',
    label: 'home_companion.knowledge_base.title',
    route: 'knowledge-base',
  },
  {
    icon: 'mdi-test-tube',
    label: 'section.testers',
    show: hasRole('developer'),
    route: 'tester',
  },
  {
    icon: 'mdi-account-multiple',
    label: 'section.user-admin',
    route: 'admin',
    show: hasRole('admin'),
  },
  {
    icon: 'mdi-door-open',
    label: 'section.impersonate-login',
    route: 'impersonateLogin',
    show: hasRole('admin'),
  },
  {
    icon: 'mdi-cog',
    label: 'section.configuration',
    route: 'configuration',
    show: hasRole('developer'),
    indicator: !config.isDefault,
  },
  // NOTE: this override will apply even if current user has dev permission
  // we must imperatively display this warning to avoid debugging headaches
  {
    icon: 'mdi-cog',
    label: 'monolith.shared.reset_config',
    show: !hasRole('developer') && !config.isDefault,
    indicator: true,
    click: () => {
      localStorage.setItem('re-local-config', '{}')
    },
  },
  {
    icon: 'mdi-card-bulleted',
    label: 'section.playground',
    show: hasRole('developer'),
    route: 'playground',
  },
])
</script>
<style lang="scss" scoped>
.drawer-content {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.drawer-scroll {
  flex: 1;
  overflow-y: auto;
}

.sidemenu-item {
  color: var(--secondary-light) !important;
  margin: 5px;
  border-radius: $button-border-radius;
  width: calc(100% - 10px);

  &:hover {
    color: var(--q-secondary) !important;
  }
}

:deep(.q-item--active) {
  background-color: var(--primary-bground);
  color: var(--q-primary) !important;
  font-weight: 900;

  &:hover {
    color: var(--q-primary) !important;
  }
}
</style>
